import type { BreadcrumbItem } from '@common/Breadcrumb/Breadcrumb';
import { Breadcrumb } from '@common/Breadcrumb/Breadcrumb';
import { ContactBox } from '@common/ContactBox/ContactBox';
import { Footer } from '@layout/Footer/Footer';
import { Header } from '@layout/Header';
import { ShopInfo } from '@layout/ShopInfo/ShopInfo';
import { ScrollToTopButton } from '@layout/ScrollToTopButton/ScrollToTopButton';
import type { NodeContact } from '@models/api/node-contact.types';
import { LocaleSelectionContextProvider } from '@hooks/useLocaleSelectionContext';
import { NewFooter } from '@layout/NewFooter/NewFooter';
import type { Footer as FooterType } from '@models/api/page-content.types';
import { useRouter } from 'next/router';
import type { HeaderData } from '@layout/Header/types';
import { useEffect, useState } from 'react';
import { isNewHeaderEnabled } from '@layout/Header/libs/header.helper';
import { fetchClientHeaderData } from '@layout/Header/libs/header.api.client';

export interface BaseLayoutProps {
  breadcrumbItems?: BreadcrumbItem[];
  contact?: NodeContact | null;
  header: HeaderData;
  footer: FooterType | null;
  children?: React.ReactNode;
}

export const BaseLayout = ({ breadcrumbItems, contact, header, footer, children }: BaseLayoutProps) => {
  const router = useRouter();
  // POP is parameter coming from the following story to make header, footer etc. dissappear
  // https://igusdev.atlassian.net/jira/software/c/projects/COM/boards/255?modal=detail&selectedIssue=COM-5734&sprint=1804
  const isPop = router.query?.POP === 'yes';
  const currentFooter = [];

  if (!isPop) {
    if (footer) {
      currentFooter.push(<NewFooter footerJson={footer} />);
    } else {
      currentFooter.push(
        <>
          <ShopInfo />
          <Footer />
        </>
      );
    }
  }

  const [clientHeader, setClientHeader] = useState<HeaderData>(header);

  // fetch detailed header data on client side
  useEffect(() => {
    const fetchClientHeader = async () => {
      if (isNewHeaderEnabled(router.locale)) {
        const response = await fetchClientHeaderData(router.locale);
        setClientHeader({ ...header, header: response });
      } else {
        setClientHeader(header);
      }
    };

    void fetchClientHeader();
  }, [header, router.locale]);

  return (
    <LocaleSelectionContextProvider>
      {!isPop && <Header header={clientHeader} />}
      <nav className="container my-5 text-base font-normal text-gray-500">
        {breadcrumbItems && !isPop && <Breadcrumb items={breadcrumbItems} addHome />}
      </nav>
      <main className="block container my-8 text-base font-normal text-gray-500 bg-white">{children}</main>
      {contact && !isPop && (
        <section className="container my-8 text-base font-normal text-gray-500">
          {/* eslint-disable-next-line react/jsx-props-no-spreading */}
          <ContactBox {...contact} />
        </section>
      )}
      {...currentFooter}
      <ScrollToTopButton />
    </LocaleSelectionContextProvider>
  );
};
